/**
 * QueueKit — the icon-led pieces shared by the work-queue tables (Batch Release, Review queue, Sample Testing).
 *
 *   SummaryTiles  clickable count tiles above a table (icon in a soft circle, big count, short label)
 *   IconLabel     a state as icon + one short word; the long words go in the tooltip
 *   CountChip     a small coloured count with an icon ("⚠ 2"); its meaning in the tooltip and aria-label
 *   ScoreRing     a 0–100 score as a small ring (red < 60, amber < 90, green)
 *   AgeLabel      "waiting" as a clock + text, amber past `warnDays`, red past `lateDays`
 *   OpenArrow     the row's open action as a round arrow button
 *
 * Colours are the status colours the queues already used; text stays in the theme's text colours.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Progress, Tooltip, theme } from 'antd';
import { ArrowRight, Clock } from 'lucide-react';

export const TONE = { red: '#dc2626', amber: '#d97706', green: '#16a34a', blue: '#2563eb', slate: '#64748b', purple: '#7c3aed' };

export function IconLabel({ Icon, color, label, title, testId }) {
  const body = (
    <span data-testid={testId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap' }}>
      {Icon ? <Icon size={16} strokeWidth={2.2} aria-hidden /> : null}
      {label}
    </span>
  );
  return title ? <Tooltip title={title}>{body}</Tooltip> : body;
}
IconLabel.propTypes = { Icon: PropTypes.elementType, color: PropTypes.string, label: PropTypes.node, title: PropTypes.node, testId: PropTypes.string };

export function CountChip({ Icon, color, n, title, testId }) {
  return (
    <Tooltip title={title}>
      <span
        aria-label={typeof title === 'string' ? title : undefined}
        data-testid={testId}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 999,
          background: `${color}14`, border: `1px solid ${color}40`, color, fontWeight: 700, fontSize: 12,
          fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        }}
      >
        {Icon ? <Icon size={13} strokeWidth={2.4} aria-hidden /> : null}{n}
      </span>
    </Tooltip>
  );
}
CountChip.propTypes = { Icon: PropTypes.elementType, color: PropTypes.string.isRequired, n: PropTypes.node, title: PropTypes.node, testId: PropTypes.string };

export function ScoreRing({ value }) {
  const { token } = theme.useToken();
  if (value == null) return <span style={{ color: token.colorTextTertiary }}>—</span>;
  return (
    <Progress
      type="circle"
      size={34}
      percent={value}
      strokeWidth={10}
      strokeColor={value < 60 ? TONE.red : value < 90 ? TONE.amber : TONE.green}
      format={(p) => <span style={{ fontSize: 11, fontWeight: 700, color: token.colorText }}>{p}</span>}
    />
  );
}
ScoreRing.propTypes = { value: PropTypes.number };

/** `days` decides the tone; `text` is what is shown (defaults to "N d"). */
export function AgeLabel({ days, text, title, warnDays = 7, lateDays = 14, late }) {
  const { token } = theme.useToken();
  if (days == null && !text) return <span style={{ color: token.colorTextTertiary }}>—</span>;
  const isLate = late ?? (days != null && days > lateDays);
  const color = isLate ? TONE.red : days != null && days > warnDays ? TONE.amber : token.colorTextSecondary;
  return <IconLabel Icon={Clock} color={color} label={text ?? `${days} d`} title={title} />;
}
AgeLabel.propTypes = { days: PropTypes.number, text: PropTypes.node, title: PropTypes.node, warnDays: PropTypes.number, lateDays: PropTypes.number, late: PropTypes.bool };

export function OpenArrow({ onClick, label = 'Open', testId }) {
  const { token } = theme.useToken();
  return (
    <Tooltip title={label}>
      <Button
        type="text"
        shape="circle"
        aria-label={label}
        data-testid={testId}
        icon={<ArrowRight size={18} color={token.colorPrimary} />}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      />
    </Tooltip>
  );
}
OpenArrow.propTypes = { onClick: PropTypes.func.isRequired, label: PropTypes.string, testId: PropTypes.string };

/**
 * tiles: [{ key, label, value, color, Icon, sub? }]. `active` is the selected key; `onSelect(key)` makes them
 * clickable (omit it for counts that do not filter). `loading` shows "…" for the values.
 */
export function SummaryTiles({ tiles, active, onSelect, loading, testId, minWidth = 140 }) {
  const { token } = theme.useToken();
  return (
    <div data-testid={testId} style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`, gap: 10, marginBottom: 12 }}>
      {tiles.map(({ key, label, value, color, Icon, sub, title }) => {
        const on = active === key;
        const clickable = !!onSelect;
        const Tag = clickable ? 'button' : 'div';
        const tile = (
          <Tag
            key={key}
            type={clickable ? 'button' : undefined}
            aria-pressed={clickable ? on : undefined}
            onClick={clickable ? () => onSelect(key) : undefined}
            data-testid={testId ? `${testId}-${key}` : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, textAlign: 'left',
              cursor: clickable ? 'pointer' : 'default', font: 'inherit', width: '100%',
              background: on ? `${color}12` : token.colorBgContainer,
              border: `1px solid ${on ? color : token.colorBorderSecondary}`,
              boxShadow: on ? `0 0 0 1px ${color}` : 'none', transition: 'all .15s',
            }}
          >
            <span style={{ width: 34, height: 34, borderRadius: '50%', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${color}1A`, color }}>
              {Icon ? <Icon size={18} strokeWidth={2.2} aria-hidden /> : null}
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: token.colorText, fontVariantNumeric: 'tabular-nums' }}>{loading ? '…' : (value ?? 0)}</span>
              <span style={{ fontSize: 12, color: token.colorTextSecondary }}>{label}</span>
              {sub ? <span style={{ fontSize: 11, color: token.colorTextTertiary }}>{sub}</span> : null}
            </span>
          </Tag>
        );
        return title ? <Tooltip key={key} title={title}>{tile}</Tooltip> : tile;
      })}
    </div>
  );
}
SummaryTiles.propTypes = {
  tiles: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.node, value: PropTypes.node, color: PropTypes.string, Icon: PropTypes.elementType, sub: PropTypes.node, title: PropTypes.node })).isRequired,
  active: PropTypes.string, onSelect: PropTypes.func, loading: PropTypes.bool, testId: PropTypes.string, minWidth: PropTypes.number,
};
